import { Injectable, signal } from '@angular/core';
import { BaseCheckInAction } from './classes/check-in-action';
import { RgEvent } from './classes/rg-event';

/** localStorage key: the mode, with the event it was set in. */
export const CHECK_IN_MODE_KEY = 'RGCheckInMode';

/** What the service needs to know about the event being worked. */
export type CheckInModeEvent = Pick<RgEvent, 'id' | 'toDate' | 'additionalData'>;

interface SavedMode {
  eventId: string;
  toDate: string | null;
  mode: BaseCheckInAction;
}

function readSavedMode(): SavedMode | null {
  try {
    const saved = JSON.parse(localStorage.getItem(CHECK_IN_MODE_KEY) ?? 'null');
    return typeof saved?.eventId === 'string' &&
      (typeof saved.toDate === 'string' || saved.toDate === null) &&
      (saved.mode === 'checkin' || saved.mode === 'checkout')
      ? saved
      : null;
  } catch {
    return null;
  }
}

/**
 * Whether the scanner is checking guests IN or OUT.
 *
 * This used to be a plain field on ScannerComponent, written by a toggle on the
 * scanning screen itself. The toggle now lives on Settings and on group
 * selection, and those are separate routed components — the scanner is destroyed
 * and re-created on every navigation (there is no RouteReuseStrategy), so a
 * control on another screen cannot write a scanner field. The state has to
 * outlive the component, which is what this service is for.
 *
 * REMEMBERED FOR ONE EVENT. The mode is saved with the event it was set in (its
 * id and its end date), and only that event gets it back: reopening the app on the
 * same event restores it; any other event starts at check-in and the saved mode is
 * dropped. It used to last only for the app session, which put an exit door back
 * to checking IN whenever the app restarted — and iOS discards a backgrounded app
 * freely, after which the app goes straight back to the scanner (Home's
 * auto-resume), so the door would carry on in the wrong direction unprompted.
 *
 * The event binding is the safety property. The API does NOT enforce the event's
 * `useCheckOut` setting — ClientCheckInApi accepts `checkout` unconditionally —
 * so a check-out mode carried into another event would silently check its guests
 * out. Within its own event it cannot linger either: an event lasts 24 hours (the
 * API sets toDate = fromDate + 24h), and an event moved to another date has a new
 * toDate, which no longer matches the saved one.
 *
 * For the same reason the mode is clamped on READ rather than only hidden in the
 * template: see `effectiveMode`.
 */
@Injectable({ providedIn: 'root' })
export class CheckInModeService {
  private readonly _mode = signal<BaseCheckInAction>('checkin');
  /** The event the mode belongs to; null until one is opened. */
  private eventId: string | null = null;
  private toDate: string | null = null;

  /** The operator's chosen mode. Prefer `effectiveMode` when acting on it. */
  readonly mode = this._mode.asReadonly();

  /**
   * Tie the mode to the event being worked. Called with the event's latest data
   * whenever it is opened, refreshed or rehydrated.
   *
   * - Another event gets its saved mode back only if it is the event, with the same
   *   dates, that the mode was set in. Otherwise it starts at check-in.
   * - The same event again (a refresh, a guard re-run) keeps the mode, so a
   *   pull-to-refresh cannot flip an exit door back to check-in. It is saved again,
   *   so storage always matches what the door is doing, new dates included.
   * - An event whose owner turned check-out off is check-in only, and the choice is
   *   dropped: turning check-out back on later must not silently resume checking
   *   guests out.
   */
  useEvent(event: CheckInModeEvent | null | undefined): void {
    const id = event?.id ?? null;
    const toDate = event?.toDate ?? null;
    if (id !== this.eventId) {
      const saved = readSavedMode();
      const own = id !== null && saved?.eventId === id && saved.toDate === toDate;
      this.eventId = id;
      this._mode.set(own ? saved.mode : 'checkin');
      if (!own) {
        localStorage.removeItem(CHECK_IN_MODE_KEY);
      }
    }
    this.toDate = toDate;
    if (event && !event.additionalData?.settings?.useCheckOut) {
      this._mode.set('checkin');
    }
    this.save();
  }

  setMode(mode: BaseCheckInAction): void {
    this._mode.set(mode);
    this.save();
  }

  /**
   * The mode that may actually be ACTED on, given the event's check-out setting.
   *
   * Hiding the toggle behind `@if (useCheckOut)` is not enough on its own: the
   * mode outlives any one screen, and the settings the scanner reads can be newer
   * than the last `useEvent`. The server would still honour a check-out. Clamping
   * here means every consumer — the QR payload, the per-row buttons, the result
   * card — is safe by construction.
   */
  effectiveMode(useCheckOut: boolean | undefined): BaseCheckInAction {
    return useCheckOut ? this._mode() : 'checkin';
  }

  /** Back to check-in, and forget the saved mode. Called when the operator leaves the event. */
  reset(): void {
    this._mode.set('checkin');
    this.eventId = null;
    this.toDate = null;
    localStorage.removeItem(CHECK_IN_MODE_KEY);
  }

  /** Nothing is saved before an event is opened: a mode needs an event to belong to. */
  private save(): void {
    if (this.eventId !== null) {
      const saved: SavedMode = { eventId: this.eventId, toDate: this.toDate, mode: this._mode() };
      localStorage.setItem(CHECK_IN_MODE_KEY, JSON.stringify(saved));
    }
  }
}
